import React, { ReactNode, useEffect, useState } from 'react';
import { usePersonalizeStore } from '../../src/stores/personalizeStore';
import { InternalLinks } from '../common/enums/common';
import { useRouter } from '../common/hooks/useRouter';
import ErrorBoundary from '../components/basic/errorBoundary';
import PageTitle from '../components/basic/pageTitle';
import AlertBanner from '../components/complex/alertBanner';
import Footer from '../components/complex/footer';
import Header from '../components/complex/header';
import { IHeaderProps } from '../components/complex/header/IHeader';
import { contentToHeader } from '../components/complex/header/util';
import { MarketingGrid } from '../components/complex/marketingGrid';
import NotificationBanner from '../components/complex/notification';
import { useConfig } from '../providers/config-provider';
import { useContent } from '../providers/content/hook';
import { TyRegion } from '../types';
import { safeGet } from '../utils/objectUtils';
import { getPageKeys } from '../utils/page-map';
import { REGION_MAP, getRegionFromUri } from '../utils/regionUtils';
import unwrapContentStackResponse from '../utils/unwrapCSPageModels';
import { capitalizeFirstLetter } from '../components/complex/productTile/productNameAndPrice';
import {
  unwrapNotification,
  unwrapPageFooter,
  unwrapPageHeader,
} from '../utils/unwrapLayoutModels';
import LocalesProvider from '../components/complex/header/locales-provider';
import useSplunkLogger from '../common/hooks/useSplunkLogger';
import { splunk_message } from '../common/splunk_constant';
export enum Layout {
  HOME = 'Home',
  PDPcontact = 'PDP Contacts Page',
  PDPFrames = 'PDP Frames Page',
  PLPFrames = 'PLP Brand Page',
  OrderConfirmation = 'Order Confirmation',
  UserPersonal = 'User Personal',
  UserAddress = 'User Addresses',
}
export default function PageLayout({
  children,
  location,
  data,
}: Readonly<{
  children: ReactNode;
  location: any;
  data?: any;
}>): JSX.Element {
  const { content, dispatch, service } = useContent();
  const brandFriendly = usePersonalizeStore.getState().brand;
  const [selectedLocales, setSelectedLocales] = useState(data);

  const { config } = useConfig();
  const { navigate } = useRouter();
  const { splunkLogger } = useSplunkLogger();
  useEffect(() => {
    // Ensure required dependencies are ready before fetching
    if (!service || !config) {
      return;
    }
    const fetchContent = async () => {
      let isMounted = true;
      try {
        const contentPromises = [];
        const is404Page = location.pathname.includes('404');
        const isSecureRedirectPage = [
          InternalLinks.addFavorite,
          InternalLinks.secureFramesPDP,
          InternalLinks.nonSignedInContactsPdp,
        ].some((link) => location.pathname.includes(link));
        const pageKeys = !is404Page && !isSecureRedirectPage
          ? getPageKeys(location, safeGet(children, 'props.children.props.children.props.path'))
          : null;
        contentPromises.push(service.getSiteMap(config));
        contentPromises.push(service.getHeader());
        contentPromises.push(service.getFooter());
        contentPromises.push(
          service.getNotification(REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion]),
        );
        if (pageKeys) {
          contentPromises.push(
            service.getContentPageByQuery({
              ...pageKeys,
            }),
          );
        } else {
          if (isMounted) dispatch({ type: 'PAGE', payload: null });
        }
        const results = await Promise.allSettled(contentPromises);
        const siteMapResp = results[0].status === 'fulfilled' ? results[0].value : null;
        const headerResp = results[1].status === 'fulfilled' ? results[1].value : null;
        const footerResp = results[2].status === 'fulfilled' ? results[2].value : null;
        const notificationResp = results[3].status === 'fulfilled' ? results[3].value : null;
        const pageResp =
          results.length > 4 && results[4].status === 'fulfilled'
            ? (results[4] as any).value
            : undefined;

        if (isMounted && siteMapResp) dispatch({ type: 'SITEMAP', payload: siteMapResp });
        else if (results[0].status === 'rejected') {
          splunkLogger({
            logType: 'error',
            data: {
              ...splunk_message.ERROR.FETCH_CONTENT_ERROR_IN_PAGE_LAYOUT,
              err: results[0].reason,
            },
          });
        }

        if (isMounted && headerResp)
          dispatch({ type: 'HEADER', payload: unwrapPageHeader(headerResp) });
        if (isMounted && footerResp && siteMapResp)
          dispatch({ type: 'FOOTER', payload: unwrapPageFooter(footerResp, siteMapResp) });
        if (isMounted && notificationResp)
          dispatch({ type: 'NOTIFICATION', payload: unwrapNotification(notificationResp) });

        // Only dispatch PAGE when content exists (avoids undefined payloads on redirects/404s)
        if (isMounted && pageResp && pageResp.response && siteMapResp) {
          const { response, contentType, all } = pageResp as any;
          dispatch({
            type: 'PAGE',
            payload: unwrapContentStackResponse(response, contentType, all, siteMapResp),
          });
        }
        console.log('Page load Successfully');
        splunkLogger({
          logType: 'info',
          data: splunk_message.SUCCESS.FETCH_CONTENT_SUCCESS_IN_PAGE_LAYOUT,
        });
      } catch (err) {
        console.log(err);
        navigate(InternalLinks.globalError);
        splunkLogger({
          logType: 'error',
          data: { ...splunk_message.ERROR.FETCH_CONTENT_ERROR_IN_PAGE_LAYOUT, err },
        });
      }
      return () => {
        isMounted = false;
      };
    };
    fetchContent();
    //[children, dispatch, location, service]);
  }, [location.pathname, service]);
  const footerProps = content?.footer;
  const marketingProps = content?.page?.marketingGrid;
  let pageTitle = content?.page?.pageTitle;
  const notification = content?.notification?.events;
  const siteFooterlogo = content?.header?.logo;
  if (!content || !service) return <></>;
  if (pageTitle === Layout.PDPFrames || (pageTitle === Layout.PLPFrames && brandFriendly)) {
    pageTitle = capitalizeFirstLetter(brandFriendly);
  }
  const headerPropsByContent = contentToHeader(content);
  return (
    <ErrorBoundary>
      <React.StrictMode>
        <LocalesProvider value={{ selectedLocales, setSelectedLocales }}>
          <NotificationBanner notificationProps={notification} />
          <Header headerProps={headerPropsByContent as IHeaderProps} />
          <AlertBanner color="alter-banner_red" />
          {/*<Breadcrumb links={getURLSegments()} items={breadcrumbProps} /> */}
          {pageTitle &&
            pageTitle !== Layout.UserPersonal &&
            pageTitle !== Layout.HOME &&
            pageTitle !== Layout.PDPcontact &&
            pageTitle !== Layout.UserAddress &&
            pageTitle !== Layout.OrderConfirmation && <PageTitle title={pageTitle} />}
          <main id="landing-page" tabIndex={-1}>
            {children !== false && <>{children}</>}
          </main>
          <aside role="complementary">
            <MarketingGrid marketingProps={marketingProps} />
          </aside>
          <Footer footerProps={{ ...footerProps, siteFooterlogo }} />
        </LocalesProvider>
      </React.StrictMode>
    </ErrorBoundary>
  );
}
